// services/overtime.js — извънреден труд: заплащане (чл. 262 КТ) и лимити
// (чл. 146 КТ). Чисти функции; работните дни идват само от holidays.js.
//
// Нормативна основа (сверена в актуалната редакция на 25.08.2026):
// - чл. 262, ал. 1 КТ: увеличение не по-малко от 50% (работни дни), 75%
//   (почивни дни), 100% (официални празници), 50% при СИРВ; ал. 2 —
//   базата е възнаграждението по трудовия договор, освен друга уговорка.
// - чл. 146 КТ: до 150 ч. годишно; с КТД по чл. 51б — до 300 ч. (нова
//   възможност от ДВ 107/2020, в сила 01.01.2021); и не повече от 30 ч.
//   дневен / 20 ч. нощен в месец, 6/4 в седмица, 3/2 в два последователни
//   работни дни.

const { round2 } = require('./totals');
const holidays = require('./holidays');

// Минималните увеличения по чл. 262, ал. 1 КТ, в проценти. По-високи може
// (уговорка/КТД); по-ниски — не.
const UPLIFT = { workday: 50, weekend: 75, holiday: 100, sirv: 50 };

// Лимитите по чл. 146 КТ. yearKtd — таванът с колективен трудов договор
// по чл. 51б (ДВ 107/2020).
const LIMITS = {
  year: 150, yearKtd: 300,
  monthDay: 30, monthNight: 20,
  weekDay: 6, weekNight: 4,
  twoDaysDay: 3, twoDaysNight: 2,
};

// Часовата ставка от месечната заплата: заплата / (работни дни в месеца ×
// часове на ден). Работните дни са на КОНКРЕТНИЯ месец (през holidays.js) —
// затова една и съща заплата дава различна ставка през различните месеци.
function hourlyRate(opts) {
  const o = opts || {};
  const salary = Number(o.monthlySalary);
  if (!(salary > 0)) throw new Error('Въведете месечна заплата.');
  const y = Number(o.year), m = Number(o.month);
  if (!Number.isInteger(y) || !Number.isInteger(m) || m < 1 || m > 12) throw new Error('Невалиден месец/година.');
  const hoursPerDay = Number(o.hoursPerDay) || 8;
  const pad2 = (n) => (n < 10 ? '0' : '') + n;
  const from = y + '-' + pad2(m) + '-01';
  const to = y + '-' + pad2(m) + '-' + pad2(new Date(Date.UTC(y, m, 0)).getUTCDate());
  const workDays = holidays.workingDaysBetween(from, to, o.overrides);
  const hours = workDays * hoursPerDay;
  return { workDays, hours, rate: hours > 0 ? salary / hours : 0 };
}

// Заплащане за извънредните часове: база + увеличение. Ставката се подава
// точна (не закръглена) — закръглят се САМО крайните суми (round2).
function overtimePay(opts) {
  const o = opts || {};
  const hours = Number(o.hours);
  const rate = Number(o.rate);
  if (!(hours >= 0)) throw new Error('Невалидни часове.');
  if (!(rate >= 0)) throw new Error('Невалидна часова ставка.');
  const key = String(o.dayType || 'workday');
  if (!(key in UPLIFT)) throw new Error('Непознат вид ден: ' + key);
  const upliftPercent = o.upliftPercent != null ? Number(o.upliftPercent) : UPLIFT[key];
  if (upliftPercent < UPLIFT[key]) throw new Error('Увеличението (' + upliftPercent + '%) е под минимума ' + UPLIFT[key] + '% по чл. 262 КТ.');
  const base = round2(hours * rate);
  const uplift = round2(hours * rate * upliftPercent / 100);
  // Общото е сборът на ПОКАЗАНИТЕ редове (преглед 17.09.2026): три отделни
  // закръгляния даваха фиш, който не се събира (59.52 + 29.76 ≠ 89.29).
  return { upliftPercent, base, uplift, total: round2(base + uplift) };
}

// Проверка на лимитите по чл. 146 — връща предупреждения, не блокира:
// калкулаторът информира, преценката е на счетоводителя.
function overtimeLimitsCheck(opts) {
  const o = opts || {};
  const warnings = [];
  const yearCap = o.hasKtd ? LIMITS.yearKtd : LIMITS.year;
  if (o.yearHours != null && Number(o.yearHours) > yearCap) {
    warnings.push('Годишният извънреден труд (' + o.yearHours + ' ч.) надхвърля лимита от ' + yearCap + ' ч.'
      + (o.hasKtd ? ' (таванът с КТД по чл. 51б е 300 ч.)' : ' (чл. 146, ал. 1 КТ; с КТД по чл. 51б — до 300 ч.)'));
  }
  if (o.monthDayHours != null && Number(o.monthDayHours) > LIMITS.monthDay) {
    warnings.push('Дневният извънреден труд за месец (' + o.monthDayHours + ' ч.) надхвърля 30 ч. (чл. 146 КТ).');
  }
  if (o.monthNightHours != null && Number(o.monthNightHours) > LIMITS.monthNight) {
    warnings.push('Нощният извънреден труд за месец (' + o.monthNightHours + ' ч.) надхвърля 20 ч. (чл. 146 КТ).');
  }
  if (o.weekDayHours != null && Number(o.weekDayHours) > LIMITS.weekDay) {
    warnings.push('Дневният извънреден труд за седмица (' + o.weekDayHours + ' ч.) надхвърля 6 ч. (чл. 146 КТ).');
  }
  if (o.weekNightHours != null && Number(o.weekNightHours) > LIMITS.weekNight) {
    warnings.push('Нощният извънреден труд за седмица (' + o.weekNightHours + ' ч.) надхвърля 4 ч. (чл. 146 КТ).');
  }
  if (o.twoDaysDayHours != null && Number(o.twoDaysDayHours) > LIMITS.twoDaysDay) {
    warnings.push('Извънредният труд в два последователни работни дни (' + o.twoDaysDayHours + ' ч.) надхвърля 3 ч. дневен (чл. 146 КТ).');
  }
  return { warnings };
}

module.exports = { hourlyRate, overtimePay, overtimeLimitsCheck, UPLIFT, LIMITS };
